import type { PmisProgressCurve, PmisProgressCurvePoint } from '@/pages/sites/pmis/queries';

/**
 * 공정율 곡선을 그리기 위한 계산 (DOC-33).
 *
 * <h2>여기서 공정율을 계산하지 않는다</h2>
 * 예정율·실적율은 <b>서버가 접어서</b> 준다(`WbsService#progressCurve`). 이 파일이 하는 일은
 * 그 숫자를 <b>화면 좌표로 옮기는 것</b>뿐이다. 같은 식을 양쪽에 두면 표의 "현장 전체 31.2%" 와
 * 그래프의 오늘 지점이 언젠가 갈라진다.
 *
 * <h2>왜 SVG 를 손으로 그리나</h2>
 * 차트 라이브러리를 하나 넣으면 번들이 수백 KB 늘어난다. 여기 필요한 것은 <b>선 두 개와 격자</b>다.
 * 그 정도는 `<path d="M … L …">` 한 줄로 끝나고, 인쇄·확대에도 깨지지 않는다.
 */

/**
 * 그리는 영역의 <b>기본</b> 크기. 재지 못하는 자리(가져오기 미리보기·보할 그래프)가 이 값을 쓴다.
 *
 * <p>공정율 그래프는 이 값을 쓰지 않는다 — 제 상자를 재서 그만큼 그린다({@link plotOf}).
 */
export const CHART = {
  width: 720,
  height: 240,
  /** 축 이름이 들어갈 여백. 왼쪽은 "100%", 아래는 날짜. */
  padLeft: 38,
  padRight: 12,
  padTop: 12,
  padBottom: 24,
} as const;

export const PLOT_WIDTH = CHART.width - CHART.padLeft - CHART.padRight;
export const PLOT_HEIGHT = CHART.height - CHART.padTop - CHART.padBottom;

/**
 * 그릴 자리 — <b>재서 정한다</b> (사용자 지시 2026-09-15 「이 표 반응형으로 하라니까?」).
 *
 * <h2>viewBox 를 고정하면 넓혀도 그래프가 안 커진다</h2>
 * SVG 는 기본이 {@code preserveAspectRatio="xMidYMid meet"} 라, 상자가 viewBox 보다 넓으면
 * 비율을 지키느라 <b>가운데로 몰고 양옆을 비운다</b>. 곡선 패널을 가로 전체로 내보냈더니
 * 그래프는 720×240 비율 그대로 가운데 박혀 있고 좌우가 하얗게 남았다 — 「반응형이 아니다」로 겪는다.
 *
 * <p>그래서 viewBox 를 <b>상자와 같은 크기</b>로 준다. 그러면 한 단위가 곧 한 화면 픽셀이라
 * 늘어나는 것도 찌그러지는 것도 없고, 글자 크기가 배율을 타지 않아 좁은 화면에서도 그대로 읽힌다.
 *
 * @param width  잰 가로. 0 이나 음수면(아직 안 쟀다) 기본값으로 물러선다
 * @param height 잰 세로
 */
export function plotOf(width: number, height: number) {
  const w = width > 0 ? width : CHART.width;
  const h = height > 0 ? height : CHART.height;
  return {
    width: w,
    height: h,
    // 여백보다 좁은 상자에서 음수가 나오지 않게 바닥을 둔다 — 음수 폭은 path 를 통째로 뒤집는다.
    plotWidth: Math.max(1, w - CHART.padLeft - CHART.padRight),
    plotHeight: Math.max(1, h - CHART.padTop - CHART.padBottom),
  };
}

export type Plot = ReturnType<typeof plotOf>;

/** 기본 자리 — 재지 않는 화면이 쓴다. */
const DEFAULT_PLOT: Plot = {
  width: CHART.width, height: CHART.height, plotWidth: PLOT_WIDTH, plotHeight: PLOT_HEIGHT,
};

/** 가로: 점 번호 → x. 점이 하나뿐이면 가운데 놓는다(0 으로 나누지 않는다). */
export function xAt(index: number, total: number, plot: Plot = DEFAULT_PLOT): number {
  if (total <= 1) return CHART.padLeft + plot.plotWidth / 2;
  return CHART.padLeft + (plot.plotWidth * index) / (total - 1);
}

/** 세로: 0~100% → y. 위가 100 이다. */
export function yAt(rate: number, plot: Plot = DEFAULT_PLOT): number {
  const clamped = Math.max(0, Math.min(100, rate));
  return CHART.padTop + plot.plotHeight * (1 - clamped / 100);
}

/**
 * 선 하나의 `path`.
 *
 * <p><b>값이 없는 점(null)에서 선을 끊는다.</b> 이어 버리면 "아직 오지 않은 날" 이 0% 로 보이거나,
 * 빠진 구간이 직선으로 메워져 없던 실적이 있는 것처럼 그려진다.
 */
export function linePath(values: Array<number | null>, plot: Plot = DEFAULT_PLOT): string {
  let path = '';
  let penDown = false;
  values.forEach((value, index) => {
    if (value == null) {
      penDown = false;
      return;
    }
    const command = penDown ? 'L' : 'M';
    path += `${path ? ' ' : ''}${command}${xAt(index, values.length, plot).toFixed(1)},`
      + `${yAt(value, plot).toFixed(1)}`;
    penDown = true;
  });
  return path;
}

/**
 * 가로축에 이름을 붙일 점들 — <b>달이 바뀌는 자리</b>.
 *
 * <p>모든 점에 날짜를 적으면 글자가 겹친다. 주 단위 점이 수십 개여도 달은 몇 개뿐이다.
 * 이름이 너무 촘촘해지면(달이 12개 넘게) 건너뛴다.
 *
 * <p><b>해가 바뀌는 자리와 맨 앞에는 연도를 붙인다.</b> 3년짜리 공사에서 "8월 … 8월 … 8월" 만
 * 늘어놓으면 어느 해인지 알 수 없고, 연도를 잘못 읽었을 때 그 사실이 보이지 않는다.
 */
export function monthTicks(dates: string[]): Array<{ index: number; label: string }> {
  const ticks: Array<{ index: number; label: string; year: boolean }> = [];
  let lastMonth = '';
  let lastYear = '';

  dates.forEach((date, index) => {
    const month = date.slice(0, 7);
    if (month === lastMonth) return;

    const year = date.slice(0, 4);
    const newYear = year !== lastYear;
    lastMonth = month;
    lastYear = year;
    ticks.push({ index, label: `${Number(date.slice(5, 7))}월`, year: newYear });
  });

  const step = Math.ceil(ticks.length / 12);
  const kept = step <= 1 ? ticks : ticks.filter((tick, index) => tick.year || index % step === 0);

  return kept.map((tick) => ({
    index: tick.index,
    // 해가 바뀌는 자리(그리고 맨 앞)에만 연도를 적는다 — 매번 적으면 글자가 겹친다.
    label: tick.year ? `${dates[tick.index]?.slice(2, 4)}년 ${tick.label}` : tick.label,
  }));
}

/** 오늘에 가장 가까운 점. 세로 기준선을 그 자리에 세운다. 점이 없으면 null. */
export function todayIndex(dates: string[], today: string): number | null {
  let found: number | null = null;
  dates.forEach((date, index) => {
    if (date <= today) found = index;
  });
  return found;
}

/**
 * 오늘 시점의 예정 대비 실적 — 화면 위에 한 줄로 적는 말.
 *
 * <p>곡선을 보여 주는 이유는 모양이 아니라 <b>지금 앞서는가 밀리는가</b>다.
 * 실적이 아직 없으면 null 을 돌려준다 — 0% 밀렸다고 말하지 않는다.
 *
 * <p>실적은 <b>주간회의 보고 공정율</b>이다(사용자 지시 2026-10-01). 작업일보 롤업(`actualRate`)은
 * 화면에서 뺐으므로 여기서도 보지 않는다 — 그래프 위 한 줄과 선이 다른 값을 말하면 안 된다.
 */
export function currentGap(curve: PmisProgressCurve, today: string): { planned: number; actual: number; gap: number } | null {
  const index = todayIndex(curve.points.map((point) => point.date), today);
  if (index == null) return null;

  const point: PmisProgressCurvePoint | undefined = curve.points[index];
  if (!point || point.reportedRate == null) return null;

  return { planned: point.plannedRate, actual: point.reportedRate, gap: point.reportedRate - point.plannedRate };
}
